import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useModals } from 'almanac-ui/Modal'
import { VisualStylesModal } from 'components/VisualStylesModal'
import Styles from './BrandVisualStylePreview.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Check02 } from 'blaze-ui/icons/16'
import { Images } from 'blaze-ui/icons/24'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { StyleImageLoader } from '../BrandVisualStyleSelection/StyleImageLoader'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { TopBar } from '../TopBar/TopBar'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'
import { startCase } from 'lodash'

export interface BrandVisualStylePreviewProps {
  onNext: (selectedVisualStyle: StyleGenerated, stylesGenerated: StyleGenerated[]) => void
  onBack: () => void
  onProvideBackHandler?: (handler: (() => void) | null) => void
  initialStylesGenerated?: StyleGenerated[]
  initialSelectedStyle?: StyleGenerated
  sourceImage: MediaFile | null
  brandKitId?: number
}

const FAKE_DURATIONS = [65000, 85000, 75000]
const DEFAULT_STYLES = ['Morning Contrast', 'Sunset Studio', 'Black and White Minimal']
const FAKE_PROGRESS_MAX = 95
const MOBILE_FAKE_DURATION = 70000

export const BrandVisualStylePreview = ({
  onNext,
  onBack,
  onProvideBackHandler,
  initialStylesGenerated = [],
  initialSelectedStyle,
  sourceImage,
  brandKitId,
}: BrandVisualStylePreviewProps) => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const { openModal } = useModals()
  const { data: visualStyles } = useVisualStyles()
  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId || 0 })
  const { isMobile } = useResponsiveDesign()

  const [stylesGenerated, setStylesGenerated] = useState<StyleGenerated[]>(sourceImage ? [] : initialStylesGenerated)
  const [completionState, setCompletionState] = useState<boolean[]>([false, false, false])
  const [selectedVisualStyleId, setSelectedVisualStyleId] = useState<number | null>(
    initialSelectedStyle?.id || stylesGenerated[0]?.id,
  )
  const [generationDone, setGenerationDone] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showMobileLoading, setShowMobileLoading] = useState<boolean>(isMobile && !!sourceImage)
  const [mobileProgress, setMobileProgress] = useState<number>(0)
  const [showMobileSeeAll, setShowMobileSeeAll] = useState<boolean>(false)
  const [seeAllSelectedId, setSeeAllSelectedId] = useState<number | null>(null)

  const fakeProgressAnimationRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasCompletedMobileLoadingRef = useRef(false)
  const hasSetGenerationDoneRef = useRef(false)

  const displayedVisualStyles = useMemo(() => {
    if (initialStylesGenerated.length > 0) {
      return initialStylesGenerated.slice(0, 3)
    }
    return visualStyles?.slice(0, 3) || []
  }, [initialStylesGenerated, visualStyles])
  const allComplete = useMemo(() => completionState.every(Boolean), [completionState])

  const styled1 = useStyledImage(sourceImage || undefined, displayedVisualStyles[0]?.id ?? null, brandKitId)
  const styled2 = useStyledImage(sourceImage || undefined, displayedVisualStyles[1]?.id ?? null, brandKitId)
  const styled3 = useStyledImage(sourceImage || undefined, displayedVisualStyles[2]?.id ?? null, brandKitId)
  const styledImages = useMemo(() => [styled1, styled2, styled3], [styled1, styled2, styled3])

  const markComplete = (index: number) => {
    setCompletionState((prev) => {
      const next = [...prev]
      next[index] = true
      return next
    })
  }

  useEffect(() => {
    if (
      ((!sourceImage || !allComplete) && !isOnboardingStrategiesEnabled) ||
      displayedVisualStyles.length !== 3 ||
      hasSetGenerationDoneRef.current
    )
      return

    hasSetGenerationDoneRef.current = true
    setGenerationDone(true)
    const generatedStyles = displayedVisualStyles.map((style, index) => ({
      ...style,
      styleUrl: styledImages[index].styledUrl || '',
    }))
    setStylesGenerated(generatedStyles)
    setSelectedVisualStyleId(displayedVisualStyles[0]?.id)
  }, [sourceImage, allComplete, displayedVisualStyles, styledImages, isOnboardingStrategiesEnabled])

  useEffect(() => {
    if (stylesGenerated.length || sourceImage || !visualStyles) return

    let defaultStyles = DEFAULT_STYLES.map((name) => visualStyles.find((style) => style.name === name)).filter(
      (style): style is NonNullable<typeof style> => style !== undefined,
    )

    if (!defaultStyles.length) {
      defaultStyles = visualStyles.slice(0, 3)
    }

    if (defaultStyles.length === 3) {
      setSelectedVisualStyleId(defaultStyles[0].id)
      setStylesGenerated(
        defaultStyles.map((style) => ({
          ...style,
          styleUrl: style.cover_image_url,
          metadata: style.metadata,
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceImage, visualStyles])

  useEffect(() => {
    if (!sourceImage) return
    hasSetGenerationDoneRef.current = false
    setCompletionState([false, false, false])
    setGenerationDone(false)
    if (isMobile) {
      setShowMobileLoading(true)
      setMobileProgress(0)
      hasCompletedMobileLoadingRef.current = false
    }
  }, [sourceImage, isMobile])

  useEffect(() => {
    if (!isMobile || !showMobileLoading || !sourceImage) return

    const startTime = Date.now()

    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min((elapsed / MOBILE_FAKE_DURATION) * FAKE_PROGRESS_MAX, FAKE_PROGRESS_MAX)
      setMobileProgress(Math.round(progress))

      if (progress >= FAKE_PROGRESS_MAX && fakeProgressAnimationRef.current !== null) {
        clearInterval(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }
    }

    fakeProgressAnimationRef.current = setInterval(tick, 250)

    return () => {
      if (fakeProgressAnimationRef.current !== null) {
        clearInterval(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }
    }
  }, [isMobile, showMobileLoading, sourceImage])

  useEffect(() => {
    if (!isMobile || !showMobileLoading || !allComplete || hasCompletedMobileLoadingRef.current) return

    hasCompletedMobileLoadingRef.current = true

    if (fakeProgressAnimationRef.current !== null) {
      clearInterval(fakeProgressAnimationRef.current)
      fakeProgressAnimationRef.current = null
    }

    const startProgress = mobileProgress
    const startTime = Date.now()
    const duration = 400
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      const elapsed = Date.now() - startTime
      const progress = Math.min(startProgress + (100 - startProgress) * (elapsed / duration), 100)

      if (progress >= 100) {
        setMobileProgress(100)
        setTimeout(() => {
          setShowMobileLoading(false)
        }, 500)
      } else {
        setMobileProgress(Math.round(progress))
        setTimeout(tick, 30)
      }
    }

    setTimeout(tick, 30)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, showMobileLoading, allComplete])

  const handleBack = useCallback(() => {
    if (showMobileSeeAll) {
      setShowMobileSeeAll(false)
    } else {
      onBack()
    }
  }, [showMobileSeeAll, onBack])

  useEffect(() => {
    if (!onProvideBackHandler || !isMobile) return
    onProvideBackHandler(handleBack)
  }, [handleBack, onProvideBackHandler, isMobile])

  const handleStyleSelection = (styleId: number | null) => {
    if (!styleId) return

    const existingIndex = stylesGenerated.findIndex((s) => s.id === styleId)

    if (existingIndex !== -1) {
      if (existingIndex > 2) {
        setStylesGenerated([
          stylesGenerated[0],
          stylesGenerated[existingIndex],
          ...stylesGenerated.slice(1, existingIndex),
          ...stylesGenerated.slice(existingIndex + 1),
        ])
      }
      setSelectedVisualStyleId(styleId)
      return
    }

    const newStyleToAdd = visualStyles?.find((s) => s.id === styleId)
    if (newStyleToAdd) {
      const newStyle: StyleGenerated = {
        id: newStyleToAdd.id,
        name: newStyleToAdd.name,
        cover_image_url: newStyleToAdd.cover_image_url,
        styleUrl: newStyleToAdd.cover_image_url,
        metadata: newStyleToAdd.metadata,
      }
      setStylesGenerated([stylesGenerated[0], newStyle, ...stylesGenerated.slice(2)])
      setSelectedVisualStyleId(styleId)
    }
  }

  const handleNext = async () => {
    if (!selectedVisualStyleId || isSaving) return

    const selectedStyle = stylesGenerated.find((s) => s.id === selectedVisualStyleId) || {
      id: selectedVisualStyleId,
      styleUrl: '',
      name: '',
      cover_image_url: '',
      metadata: {
        color: '',
        contrast: '',
        display_description: '',
        focus: '',
        grain: '',
        lighting: '',
      },
    }

    if (brandKitId) {
      setIsSaving(true)
      try {
        await updateBrandKit({
          content_preferences: {
            ...brandKitData?.brand_kit?.content_preferences,
            apply_visual_style: true,
            image_media_priority: 'only_my_brand_kit',
          },
        })
      } finally {
        setIsSaving(false)
      }
    }

    onNext(selectedStyle, stylesGenerated)
  }

  const isButtonDisabled = (!!sourceImage && !generationDone) || isSaving

  const getMobileLoadingStage = () => {
    if (mobileProgress < 30) return 'Analyzing source image...'
    if (mobileProgress < 60) return 'Generating styles...'
    if (mobileProgress < 90) return 'Applying transformations...'
    return 'Finalizing...'
  }

  const mobileLoaders = isMobile && sourceImage && (
    <div style={{ position: 'fixed', top: -9999, left: -9999, pointerEvents: 'none' }}>
      {displayedVisualStyles.map((style, index) => (
        <StyleImageLoader
          key={`${sourceImage.id}-${style.id}`}
          sourceImage={sourceImage}
          stylesGeneration={styledImages[index]}
          onTransformedImageLoaded={() => markComplete(index)}
          withTag={false}
          fakeDuration={FAKE_DURATIONS[index]}
        />
      ))}
    </div>
  )

  if (isMobile && showMobileLoading && sourceImage) {
    return (
      <>
        <div className={Styles.mobileLoadingScreen} data-visual-style-page="true">
          <div className={Styles.mobileTopBar} />
          <div className={Styles.mobileLoadingBackground}>
            <img src={sourceImage.url} alt="" className={Styles.mobileLoadingBackgroundImage} />
            <div className={Styles.mobileLoadingGradient} />
          </div>
          <div className={Styles.mobileLoadingContent}>
            <div className={Styles.mobileLoadingProgressContainer}>
              <ProgressBar
                percentage={mobileProgress}
                color="var(--light-100)"
                backgroundColor="var(--light-60)"
                size="xs"
                width="140px"
              />
            </div>
            <Text variant="secondary" color="var(--light-60)">
              {getMobileLoadingStage()}
            </Text>
            <Text variant="secondary" color="var(--light-60)">
              {mobileProgress}%
            </Text>
          </div>
        </div>
        {mobileLoaders}
      </>
    )
  }

  if (isMobile && showMobileSeeAll) {
    const seeAllSelectedStyle = visualStyles?.find((s) => s.id === seeAllSelectedId)

    const handleSeeAllConfirm = () => {
      if (seeAllSelectedId !== null) {
        handleStyleSelection(seeAllSelectedId)
        setShowMobileSeeAll(false)
      }
    }

    return (
      <div className={Styles.mobileSeeAllScreen} data-visual-style-page="true">
        <div className={Styles.mobileSeeAllContent}>
          <div className={Styles.mobileSeeAllGrid}>
            {visualStyles?.map((style) => (
              <div
                key={style.id}
                className={cx(Styles.mobileSeeAllCard, {
                  [Styles.selected]: seeAllSelectedId === style.id,
                })}
                onClick={() => setSeeAllSelectedId(style.id)}
              >
                <img src={style.cover_image_url} alt={style.name} />
              </div>
            ))}
          </div>
        </div>

        <div className={Styles.mobileSeeAllBottomCta}>
          <div className={Styles.mobileSeeAllStyleName}>
            <Heading level={2} color="var(--light-100)">
              {seeAllSelectedStyle?.name || ''}
            </Heading>
          </div>
          <Button
            variant="secondary"
            size="lg"
            onPress={handleSeeAllConfirm}
            isDisabled={seeAllSelectedId === null}
            className={Styles.mobileSeeAllUseButton}
          >
            Use This Style
          </Button>
        </div>
      </div>
    )
  }

  const showMobilePreview =
    isMobile && ((sourceImage && !showMobileLoading && generationDone) || (!sourceImage && stylesGenerated.length >= 3))

  if (showMobilePreview) {
    const selectedStyle = stylesGenerated.find((s) => s.id === selectedVisualStyleId)

    return (
      <div className={Styles.mobilePreviewContainer} data-visual-style-page="true">
        <div className={Styles.mobileHeroSection}>
          {selectedStyle?.styleUrl && <img src={selectedStyle.styleUrl} alt="" className={Styles.mobileHeroImage} />}
          <div className={Styles.mobileHeroGradient} />
        </div>

        <div className={Styles.mobileThumbnailsSection}>
          <div className={Styles.mobileStyleName}>
            <Heading level={2} color="var(--light-100)">
              {selectedStyle?.name}
            </Heading>
          </div>
          <div className={Styles.mobileThumbnailsContainer}>
            {stylesGenerated.slice(0, 3).map((style) => (
              <div
                key={style.id}
                className={cx(Styles.mobileThumbnail, {
                  [Styles.selected]: selectedVisualStyleId === style.id,
                })}
                onClick={() => setSelectedVisualStyleId(style.id)}
              >
                <img src={style.styleUrl} alt={style.name} />
              </div>
            ))}
            <button
              className={Styles.mobileSeeAllButton}
              onClick={() => {
                setSeeAllSelectedId(selectedVisualStyleId)
                setShowMobileSeeAll(true)
              }}
            >
              <Images size={24} color="var(--light-100)" />
              <Text variant="label" color="var(--light-100)">
                See All
              </Text>
            </button>
          </div>

          <Button
            variant="secondary"
            size="lg"
            onPress={handleNext}
            isDisabled={isButtonDisabled}
            className={Styles.mobileUseStyleButton}
          >
            {isSaving ? 'Saving...' : 'Use This Style'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <MultiStepStepLayout
        title="Here's your recommended visual style"
        description="Applied to all your content automatically. Change it anytime."
        scrollContainerClassName={Styles.scrollContainer}
        contentClassName={Styles.customContent}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <div className={Styles.footerSide}>
                  <Button variant="tertiary" size="lg" onPress={onBack}>
                    Back
                  </Button>
                </div>
                <div className={Styles.footerCenter}>
                  <Button
                    size="lg"
                    variant="secondary"
                    onPress={() =>
                      openModal(VisualStylesModal, {
                        selectedStyleId: selectedVisualStyleId,
                        onConfirm: handleStyleSelection,
                      })
                    }
                    isDisabled={isButtonDisabled}
                    color="var(--dark-80)"
                  >
                    Browse All Styles
                  </Button>
                </div>
                <div className={Styles.footerSide}>
                  <Button size="lg" onPress={handleNext} isDisabled={isButtonDisabled}>
                    {isSaving ? 'Saving...' : 'Apply this style'}
                  </Button>
                </div>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <TopBar highlightStep="yourBrand" />
          <div className={Styles.stylesContainer}>
            {sourceImage && !generationDone
              ? displayedVisualStyles.map((style, index) => (
                  <div className={Styles.styleContainer} key={style.id} style={{ cursor: 'default' }}>
                    <StyleImageLoader
                      sourceImage={sourceImage}
                      stylesGeneration={styledImages[index]}
                      onTransformedImageLoaded={() => markComplete(index)}
                      withTag={false}
                      fakeDuration={FAKE_DURATIONS[index]}
                    />
                  </div>
                ))
              : stylesGenerated.slice(0, 3).map((style, index) => (
                  <div
                    className={Styles.styleContainer}
                    key={style.id}
                    onClick={() => setSelectedVisualStyleId(style.id)}
                  >
                    <StyleImageLoader
                      staticMode
                      staticImageUrl={style.styleUrl || style.cover_image_url}
                      withTag={false}
                    />

                    <div className={Styles.styleNameContainer}>
                      <div className={Styles.styleData}>
                        <Heading color="var(--light-100)" level={1}>
                          {style.name}
                        </Heading>
                        <div className={Styles.styleMetadata}>
                          <div className={Styles.styleMetadataRow}>
                            <div className={Styles.styleMetadataItem}>
                              <span className={Styles.styleMetadataItemLabel}>Lighting</span>
                              <Text variant="secondary" color="var(--light-100)">
                                {startCase(style.metadata?.lighting)}
                              </Text>
                            </div>
                            <div className={Styles.styleMetadataItem}>
                              <span className={Styles.styleMetadataItemLabel}>Color</span>
                              <Text variant="secondary" color="var(--light-100)">
                                {startCase(style.metadata?.color)}
                              </Text>
                            </div>
                          </div>
                          <div className={Styles.styleMetadataRow}>
                            <div className={Styles.styleMetadataItem}>
                              <span className={Styles.styleMetadataItemLabel}>Contrast</span>
                              <Text variant="secondary" color="var(--light-100)">
                                {startCase(style.metadata?.contrast)}
                              </Text>
                            </div>
                            <div className={Styles.styleMetadataItem}>
                              <span className={Styles.styleMetadataItemLabel}>Grain</span>
                              <Text variant="secondary" color="var(--light-100)">
                                {startCase(style.metadata?.grain)}
                              </Text>
                            </div>
                            <div className={Styles.styleMetadataItem}>
                              <span className={Styles.styleMetadataItemLabel}>Focus</span>
                              <Text variant="secondary" color="var(--light-100)">
                                {startCase(style.metadata?.focus)}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {index === 0 && <div className={Styles.recommendedTag}>Recommended</div>}
                    {selectedVisualStyleId === style.id ? (
                      <div className={Styles.checkmark}>
                        <Check02 size={20} color="var(--dark-80)" />
                      </div>
                    ) : (
                      <div className={Styles.emptyCheckmark} />
                    )}
                  </div>
                ))}
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    </>
  )
}
